import * as React from 'react'

import { type IParseFile, type UploadOptions, nuvoSession } from '@getnuvo/importer-react'

import { type EditionData } from './types'

const INVALID_SHEET_PREFIXES = ['Lang', 'v_']

const transposeData = (data: unknown[][]): unknown[][] => {
  if (!data || data.length === 0) return data

  const maxLength = Math.max(...data.map((row) => row?.length || 0))
  const transposed: unknown[][] = []

  for (let i = 0; i < maxLength; i++) {
    transposed[i] = []
    for (let j = 0; j < data.length; j++) {
      const row = data[j]
      const transposedRow = transposed[i]
      if (transposedRow) {
        transposedRow[j] = row && row[i] !== undefined ? row[i] : ''
      }
    }
  }

  return transposed
}

interface UseNuvoSessionProps {
  importType: string
  parsedFile: IParseFile[] | undefined
  initialData?: EditionData
  loading: boolean
  nuvoImporterRef: React.RefObject<HTMLDivElement>
  headerSelection: boolean
  transpose?: boolean
}

export const useNuvoSession = ({
  importType,
  parsedFile,
  initialData,
  loading,
  nuvoImporterRef,
  headerSelection,
  transpose = false,
}: UseNuvoSessionProps) => {
  React.useEffect(() => {
    if (!parsedFile && (loading || !!initialData)) return

    const getSessionData = (): UploadOptions => {
      if (!parsedFile) return { step: 'mapping', data: [] }

      const validSheets =
        parsedFile?.[0]?.sheets?.filter((sheet) => {
          return !INVALID_SHEET_PREFIXES.some((prefix) => sheet.sheetName.startsWith(prefix))
        }) || []

      const numberOfSheets = validSheets.length
      const getStep = () => {
        if (numberOfSheets && numberOfSheets > 1) {
          return 'sheetSelection'
        }

        if (!headerSelection) {
          return 'mapping'
        }

        return 'header'
      }
      const getData = () => {
        if (numberOfSheets && numberOfSheets > 1) {
          if (transpose) {
            return parsedFile?.map((file) => ({
              ...file,
              sheets: file.sheets?.map((sheet) => ({
                ...sheet,
                data: transposeData(sheet.data || []),
              })),
            }))
          }
          return parsedFile
        }

        const sheetData = parsedFile?.[0]?.sheets[0]?.data || []
        return transpose ? transposeData(sheetData) : sheetData
      }

      return {
        step: getStep(),
        data: getData(),
      }
    }

    if (parsedFile) {
      nuvoSession.upload(getSessionData(), importType)
      nuvoSession.start(importType)
    }
  }, [parsedFile, importType, initialData, loading, headerSelection, transpose])

  React.useEffect(() => {
    if (!initialData || loading || !nuvoImporterRef.current) return

    const timeoutId = setTimeout(() => {
      const nuvoElements = nuvoImporterRef.current?.querySelector('.nuvo-tailwind')

      if (!nuvoElements) {
        throw new Error('Nuvo elements not found')
      }

      nuvoSession.upload(
        {
          step: 'review',
          data: initialData,
          headerIndex: undefined,
        },
        importType
      )
      nuvoSession.start(importType)
    }, 5000)

    return () => clearTimeout(timeoutId)
  }, [initialData, importType, loading, nuvoImporterRef])
}
